// Экран переписки (SPEC §4.2/§4.3) — ядро. Шапка (presence/typing/memberCount) + лента (MessageList)
// + композер (Composer) + контекст-меню сообщения (по правам) + просмотрщик медиа. Управляет
// «активным чатом» (openChat/closeChat подавляют unread/тост по открытому, §4.7), отмечает прочтение,
// принимает ?msg=<messageId> для доскролла (канон L). Доменное — через стор; навигация — react-router.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { Attachment, ChatId, Message, MessageId, UserId } from '@tele-chat/shared-contracts'
import {
  ChatHeader,
  ContextMenu,
  MediaViewer,
  useLayout,
  type ContextMenuAnchor,
} from '@tele-chat/design-system'
import { useSelector, useStore } from '../store/StoreContext.js'
import { useSelf } from '../auth/SessionContext.js'
import { useUserCache } from '../users/UserCacheContext.js'
import { useActiveChat } from '../app/ActiveChatContext.js'
import { useComposer, messageRefFor } from '../chat/useComposer.js'
import { useFileIntake } from '../chat/useFileIntake.js'
import { useMediaTransfers, type MediaTransfers } from '../media/MediaTransfersContext.js'
import { chatDisplay, isOptimistic } from '../chat/derive.js'
import { chatSubtitle } from '../chat/subtitle.js'
import { messageMenuItems, MSG_ACTION, QUICK_REACTIONS } from '../chat/contextMenu.js'
import { MessageList } from './MessageList.js'
import { Composer } from './Composer.js'
import { EmptyState } from '../components/EmptyState.js'
import { MSG_PARAM } from '../routing/deep-link.js'
import { strings } from '../i18n/strings.js'

interface MsgMenuState {
  message: Message
  anchor: ContextMenuAnchor
}

export function ConversationScreen(): JSX.Element {
  const params = useParams()
  const chatId = (params.chatId ?? '') as ChatId
  const [search] = useSearchParams()
  const targetMessageId = (search.get(MSG_PARAM) ?? undefined) as MessageId | undefined

  const store = useStore()
  const self = useSelf()
  const users = useUserCache()
  const navigate = useNavigate()
  const layout = useLayout()
  const { setActiveChatId } = useActiveChat()

  const chat = useSelector((s) => s.select.chat(chatId))
  const timeline = useSelector((s) => s.select.timeline(chatId))
  const typing = useSelector((s) => s.select.typingIn(chatId))

  const composer = useComposer(chatId)
  const [menu, setMenu] = useState<MsgMenuState | null>(null)
  // Загрузки с прогрессом (DLP-6): «открыть» file/audio = скачать; оригинал фото и «Скачать» в просмотрщике.
  const transfers = useMediaTransfers()

  // Приём файлов: вставка из буфера (mobile+desktop) + перетаскивание в чат/поле (только desktop).
  // Единый вход — composer.addFiles (тот же путь, что скрепка). ДО ранних return (rules-of-hooks).
  const fileIntake = useFileIntake({ onFiles: composer.addFiles, dragEnabled: layout === 'desktop' })

  // Активный чат + openChat/closeChat (подавление unread/тоста; §4.7).
  useEffect(() => {
    if (chatId.length === 0) return
    setActiveChatId(chatId)
    store.actions.openChat(chatId)
    return () => {
      store.actions.closeChat(chatId)
      setActiveChatId(null)
    }
  }, [chatId, store, setActiveChatId])

  // Начальный backfill истории при открытии чата (один раз на чат). На свежем клиенте сообщений в
  // сторе нет, а WS-sync их не добирает (курсоры строятся только по уже загруженным чатам); без этого
  // чат с историей показывал бы «Нет сообщений» (этот экран рендерит EmptyState вместо MessageList при
  // пустом таймлайне, поэтому пагинация-вверх в MessageList не помогает). Тянем последнюю страницу
  // (loadOlder с beforeSeq=undefined → сервер вернёт свежие) лишь когда сообщений ещё нет.
  const backfilled = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (chatId.length === 0 || backfilled.current.has(chatId)) return
    backfilled.current.add(chatId)
    if (store.select.timeline(chatId).length === 0) {
      void store.actions.loadOlder(chatId).catch(() => undefined)
    }
  }, [chatId, store])

  // Профили участников (для имён авторов в группе / шапки direct).
  useEffect(() => {
    if (chat === undefined) return
    for (const id of chat.memberIds) if (users.get(id) === undefined) users.ensure(id)
  }, [chat, users])

  const resolveName = useCallback(
    (userId: UserId): string => users.get(userId)?.name ?? String(userId),
    [users],
  )

  const display = useMemo(
    () =>
      chat !== undefined && self !== null
        ? chatDisplay(chat, self.id, (id) => users.get(id))
        : { title: '' },
    [chat, self, users],
  )

  const subtitle = useMemo(() => {
    if (chat === undefined || self === null) return ''
    return chatSubtitle({
      chat,
      typing,
      peerPresence: display.peerId !== undefined ? store.select.presence(display.peerId) : undefined,
      peerId: display.peerId,
      resolveUser: (id) => users.get(id),
    })
  }, [chat, self, typing, display, store, users])

  const onMarkRead = useCallback((seq: number) => store.actions.markRead(chatId, seq), [store, chatId])

  // — контекст-меню сообщения по правам —
  const menuItems = useMemo(() => {
    if (menu === null || self === null) return []
    const myRole = undefined // роль в группе — из getMembers (профиль группы); базово автор-права
    return messageMenuItems(menu.message, { isAuthor: menu.message.senderId === self.id, myRole })
  }, [menu, self])

  const onMenuSelect = useCallback(
    (id: string): void => {
      if (menu === null) return
      const m = menu.message
      switch (id) {
        case MSG_ACTION.reply:
          composer.startReply(messageRefFor(m))
          break
        case MSG_ACTION.edit:
          composer.startEdit(messageRefFor(m), m.text ?? '')
          break
        case MSG_ACTION.deleteAll:
          void store.actions.remove(m.id, 'all')
          break
        case MSG_ACTION.deleteSelf:
          void store.actions.remove(m.id, 'self')
          break
        case MSG_ACTION.copy:
          if (m.text !== undefined && typeof navigator !== 'undefined' && navigator.clipboard) {
            void navigator.clipboard.writeText(m.text)
          }
          break
        case MSG_ACTION.forward:
          // Диалог выбора чатов — открывается на /chat/:id?forward=:id (упрощённо: переслать в этот же
          // чат не имеет смысла; реальный выбор-диалог — отдельный экран, ведётся через actions.forward).
          break
        default:
          break
      }
    },
    [menu, composer, store],
  )

  const [mediaViewer, setMediaViewer] = useState<{ message: Message; index: number } | null>(null)

  // Чат ИСЧЕЗ из стора (группа удалена владельцем — WS chat.deleted) → на список чатов.
  // Хуки — ДО ранних return (rules-of-hooks).
  const hadChatRef = useRef(false)
  useEffect(() => {
    if (chat !== undefined) {
      hadChatRef.current = true
    } else if (hadChatRef.current) {
      hadChatRef.current = false
      navigate('/')
    }
  }, [chat, navigate])

  if (chatId.length === 0) {
    return <EmptyState title={strings.empty.selectChat} />
  }

  if (chat === undefined) {
    // Чат ещё не в сторе (офлайн/не загружен) — нейтральная заглушка, без падения.
    return <EmptyState title={strings.app.loading} />
  }

  return (
    <div
      data-screen="conversation"
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      {...fileIntake.dropProps}
    >
      <ChatHeader
        title={display.title}
        subtitle={subtitle.length > 0 ? subtitle : undefined}
        avatarUrl={display.avatarUrl}
        avatarColorKey={chat.id}
        online={
          chat.type === 'direct' && display.peerId !== undefined
            ? store.select.presence(display.peerId)?.state === 'online'
            : undefined
        }
        onBack={layout === 'mobile' ? () => navigate('/') : undefined}
        onSearch={() => navigate(`/search?chatId=${encodeURIComponent(chat.id)}`)}
        onOpenProfile={() => navigate(`/chat/${encodeURIComponent(chat.id)}/profile`)}
      />

      {timeline.length === 0 ? (
        <EmptyState title={strings.empty.noMessages} />
      ) : (
        <MessageList
          chatId={chatId}
          selfId={self?.id ?? ('' as UserId)}
          timeline={timeline}
          lastReadSeq={chat.myState.lastReadSeq}
          isGroup={chat.type === 'group'}
          resolveName={resolveName}
          scrollToMessageId={targetMessageId}
          onReply={(m) => composer.startReply(messageRefFor(m))}
          onReplyQuoteClick={(messageId) => navigate(`/chat/${encodeURIComponent(chat.id)}?${MSG_PARAM}=${encodeURIComponent(messageId)}`)}
          onOpenContextMenu={(message, anchor) => setMenu({ message, anchor })}
          onReact={(messageId, emoji) => void store.actions.react(messageId, emoji, true)}
          onOpenMedia={(message, index) => {
            // file/audio в просмотрщик не идут (он рисует image/video) — «открыть» их = скачать.
            // У оптимистичного сообщения серверного id ещё нет — молча игнорируем клик до коммита.
            const att = message.attachments?.[index]
            if (att !== undefined && (att.kind === 'file' || att.kind === 'audio')) {
              if (!isOptimistic(message)) transfers.download(att)
              return
            }
            setMediaViewer({ message, index })
          }}
          onMarkRead={onMarkRead}
        />
      )}

      <Composer chat={chat} composer={composer} />

      {fileIntake.isDragging ? (
        <div
          data-drop-overlay=""
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // pointerEvents:none — оверлей не перехватывает drag-события, они доходят до контейнера-зоны
            // (иначе dragleave/drop стреляли бы по оверлею и логика счётчика ломалась).
            pointerEvents: 'none',
            background: 'var(--color-overlay)',
            border: '2px dashed var(--color-accent)',
            borderRadius: 'var(--radius-card)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3)',
              color: 'var(--color-accent)',
              fontSize: 'var(--font-lg)',
              fontWeight: 'var(--weight-medium)',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: '40px', lineHeight: 1 }}>📎</span>
            <span>{strings.composer.dropHint}</span>
          </div>
        </div>
      ) : null}

      <ContextMenu
        open={menu !== null}
        items={menuItems}
        anchor={menu?.anchor ?? { x: 0, y: 0 }}
        quickReactions={QUICK_REACTIONS}
        onQuickReact={(emoji) => {
          if (menu !== null) void store.actions.react(menu.message.id, emoji, true)
        }}
        onSelect={onMenuSelect}
        onClose={() => setMenu(null)}
      />

      {mediaViewer !== null ? (
        <MediaViewerInline
          message={mediaViewer.message}
          index={mediaViewer.index}
          onClose={() => setMediaViewer(null)}
          transfers={isOptimistic(mediaViewer.message) ? undefined : transfers}
        />
      ) : null}
    </div>
  )
}

// Инлайн-просмотрщик из ленты: показывает вложения конкретного сообщения. Полноэкранный просмотрщик
// со свайпом рисует design-system MediaViewer; индекс ведём локально.
// В просмотрщик идут ТОЛЬКО image/video: file/audio он рисовать не умеет (битый <img> при свайпе
// на zip в том же сообщении) — их «открытие» перехвачено выше (скачивание). Индекс входа — по
// полному списку вложений, ремапим в индекс визуального подсписка.
function MediaViewerInline({
  message,
  index,
  onClose,
  transfers,
}: {
  message: Message
  index: number
  onClose: () => void
  /** Менеджер загрузок (DLP-6); нет — оптимистичное сообщение (серверного id ещё нет), src напрямую. */
  transfers?: MediaTransfers
}): JSX.Element | null {
  const all = message.attachments ?? []
  const items = all.filter((a) => a.kind === 'image' || a.kind === 'video')
  const opened = all[index]
  const initial = opened !== undefined ? Math.max(0, items.indexOf(opened)) : 0
  const [i, setI] = useState(initial)
  const current: Attachment | undefined = items[Math.min(Math.max(0, i), items.length - 1)]
  const hasTransfers = transfers !== undefined
  const transfersRef = useRef(transfers)
  transfersRef.current = transfers

  // DLP-6: оригинал текущего фото — старт при показе, отмена недокачанного при уходе (свайп/закрытие).
  // Зависимости — id/kind, не сам transfers: его identity меняется на каждый тик прогресса.
  useEffect(() => {
    const t = transfersRef.current
    if (t === undefined || current === undefined || current.kind !== 'image') return
    t.loadOriginal(current)
    return () => t.releaseOriginal(current.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.kind, hasTransfers])

  if (items.length === 0 || current === undefined) return null
  const transfer = transfers?.transferOf(current.id)
  return (
    <MediaViewer
      items={items}
      index={i}
      onIndexChange={setI}
      onClose={onClose}
      itemState={
        transfers !== undefined && current.kind === 'image'
          ? (transfers.originalOf(current.id) ?? { status: 'loading' })
          : undefined
      }
      onRetry={transfers !== undefined ? () => transfers.retryOriginal(current) : undefined}
      onDownload={transfers !== undefined ? () => transfers.download(current) : undefined}
      download={
        transfer?.phase === 'downloading'
          ? transfer.progress !== undefined
            ? { progress: transfer.progress }
            : {}
          : undefined
      }
      onCancelDownload={transfers !== undefined ? () => transfers.cancel(current.id) : undefined}
    />
  )
}
